/**
 * Parser for whiteboard edit operations using function-like syntax
 * Converts operation strings into JSON format for whiteboard manipulation
 */

import { inferRecordType } from '@aha-app/aha-shared';

export interface ParsedOperation {
  type: 'create' | 'update' | 'delete' | 'record';
  data: Record<string, any>;
}

export interface ParseError {
  position: number;
  content: string;
  error: string;
}

export interface ParsedArguments {
  positional: any[];
  named: Record<string, any>;
}

// Mapping of function names to non-shape object types
// All other functions default to { type: 'shape', shapeName: function_name }
const SHAPE_TYPE_MAPPING: Record<string, Record<string, string>> = {
  // Non-shape object types
  connector: { type: 'connector' },
  frame: { type: 'frame' },
  group: { type: 'group' },
  path: { type: 'path' },
  stamp: { type: 'svgStamp' },
  table: { type: 'table' },
};

// Mapping from class names to their URL paths - matches packages/whiteboard/src/tools/ToolPaste.ts regex patterns
const RECORD_TYPE_TO_PATH: Record<string, string> = {
  Feature: '/features/',
  Requirement: '/requirements/',
  Epic: '/epics/',
  Release: '/releases/',
  MasterRelease: '/master_releases/',
  'Ideas::Idea': '/ideas/',
  'Ideas::IdeaTheme': '/idea_themes/',
  Initiative: '/initiatives/',
  StrategicImperative: '/strategic_imperatives/',
  Page: '/pages/',
  KeyResult: '/key_results/',
};

/**
 * Sanitize a line by replacing encoded characters
 */
function sanitizeLine(line: string): string {
  return line.replace(/\\u0003c/g, '<').replace(/\\u0003e/g, '>');
}

/**
 * Parse a value string into its appropriate type
 */
function parseValue(valueString: string) {
  const value = valueString.trim();

  // Handle quoted strings
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    let quotedContent = value.slice(1, -1); // Remove quotes

    // Process escape sequences using JSON parsing
    try {
      // Wrap in quotes and parse as JSON string to handle escape sequences
      quotedContent = JSON.parse(`"${quotedContent}"`) as string;
    } catch {
      // If parsing fails, keep the original content
    }

    return quotedContent;
  }

  // Handle booleans
  if (value === 'true') return true;
  if (value === 'false') return false;

  // Handle numbers (including negative)
  if (/^-?\d+$/.test(value)) {
    return parseInt(value, 10);
  }
  if (/^-?\d*\.\d+$/.test(value)) {
    return parseFloat(value);
  }

  // Handle scientific notation
  if (/^-?\d+(\.\d+)?e[+-]?\d+$/i.test(value)) {
    return parseFloat(value);
  }

  // Handle JSON objects and arrays
  if (value.startsWith('{') || value.startsWith('[')) {
    try {
      return JSON.parse(value);
    } catch {
      // If JSON parsing fails, return as string
    }
  }

  // Return as string for anything else
  return value;
}

/**
 * Parse dimensions from a value (e.g., "100x50" or "100")
 */
function parseDimensions(
  value: string
): { width: number; height: number } | number | undefined {
  const match = value.match(/^(\d+)x(\d+)$/);
  if (match) {
    return { width: parseInt(match[1], 10), height: parseInt(match[2], 10) };
  }
  if (/^\d+$/.test(value)) {
    return parseInt(value, 10);
  }
}

/**
 * Parse arguments from an arguments string
 */
export function parseArguments(argsString: string): ParsedArguments {
  if (argsString.trim() === '') {
    return { positional: [], named: {} };
  }

  const args: string[] = [];
  let currentArg = '';
  /** Indicates if we are currently processing a character that's inside a quoted string */
  let inQuotes = false;
  let quoteChar: string | null = null;
  let parenDepth = 0;
  let bracketDepth = 0;
  let braceDepth = 0;

  for (let i = 0; i < argsString.length; i++) {
    const char = argsString[i];

    switch (char) {
      case '"':
      case "'":
        if (!inQuotes) {
          inQuotes = true;
          quoteChar = char;
        } else if (char === quoteChar) {
          inQuotes = false;
          quoteChar = null;
        }
        currentArg += char;
        break;

      case '(':
        if (!inQuotes) parenDepth++;
        currentArg += char;
        break;

      case ')':
        if (!inQuotes) parenDepth--;
        currentArg += char;
        break;

      case '[':
        if (!inQuotes) bracketDepth++;
        currentArg += char;
        break;

      case ']':
        if (!inQuotes) bracketDepth--;
        currentArg += char;
        break;

      case '{':
        if (!inQuotes) braceDepth++;
        currentArg += char;
        break;

      case '}':
        if (!inQuotes) braceDepth--;
        currentArg += char;
        break;

      case ',':
        if (
          !inQuotes &&
          parenDepth === 0 &&
          bracketDepth === 0 &&
          braceDepth === 0
        ) {
          args.push(currentArg.trim());
          currentArg = '';
        } else {
          currentArg += char;
        }
        break;

      default:
        currentArg += char;
    }
  }

  // Add the last argument
  if (currentArg.trim() !== '') {
    args.push(currentArg.trim());
  }

  // Separate positional and named arguments
  const positional: ReturnType<typeof parseValue>[] = [];
  const named: Record<string, any> = {};

  for (const arg of args) {
    if (
      arg.includes('=') &&
      !arg.startsWith('"') &&
      !arg.startsWith("'") &&
      !arg.startsWith('{')
    ) {
      // Named argument
      const [key, ...valueParts] = arg.split('=');
      const value = valueParts.join('='); // Handle values that contain '='
      named[key.trim()] = parseValue(value.trim());
    } else {
      // Positional argument
      positional.push(parseValue(arg));
    }
  }

  return { positional, named };
}

/**
 * Build shape data from configuration and arguments
 */
function buildShapeData(
  shapeConfig: Record<string, string>,
  args: ParsedArguments
): Record<string, any> {
  const { positional, named } = args;

  // first argument is always an id
  const shapeId = String(positional[0]);
  const positionOffset = 1;

  // Start with base shape configuration
  const shapeData: Record<string, any> = { ...shapeConfig, id: shapeId };

  // Process positional arguments based on shape type (adjusted for ID offset)
  if (positional.length >= positionOffset + 2) {
    // Convert to number (handles scientific notation)
    const leftValue = positional[positionOffset];
    const topValue = positional[positionOffset + 1];

    shapeData.left =
      typeof leftValue === 'number'
        ? leftValue
        : parseInt(String(leftValue), 10);
    shapeData.top =
      typeof topValue === 'number' ? topValue : parseInt(String(topValue), 10);
  }

  // Handle dimensions (3rd argument after ID)
  if (positional.length >= positionOffset + 3) {
    const dimensions = parseDimensions(positional[positionOffset + 2]);
    if (
      typeof dimensions === 'object' &&
      dimensions.width &&
      dimensions.height
    ) {
      shapeData.width = dimensions.width;
      shapeData.height = dimensions.height;
    } else if (typeof dimensions === 'number') {
      // Single dimension - make it square
      shapeData.width = dimensions;
      shapeData.height = dimensions;
    }
  }

  // Handle text content (4th argument after ID) - but check if it's a JSON object first
  if (
    positional.length >= positionOffset + 4 &&
    positional[positionOffset + 3]
  ) {
    if (typeof positional[positionOffset + 3] === 'object') {
      // If 4th argument is a JSON object, merge it into shape data
      Object.assign(shapeData, positional[positionOffset + 3]);
    } else {
      // Otherwise treat as text content
      shapeData.textContent = String(positional[positionOffset + 3]);
    }
  }

  // Handle any additional positional arguments as JSON objects
  if (positional.length > positionOffset + 4) {
    for (let i = positionOffset + 4; i < positional.length; i++) {
      if (typeof positional[i] === 'object') {
        Object.assign(shapeData, positional[i]);
      }
    }
  }

  return { ...shapeData, ...named };
}

/**
 * Normalize a record URL
 */
function normalizeRecordUrl(url: string): string {
  if (url.startsWith('/')) {
    return url;
  }

  const recordType = inferRecordType(url);
  const pathPrefix = recordType ? RECORD_TYPE_TO_PATH[recordType] : null;

  if (pathPrefix) {
    return `${pathPrefix}${url}`;
  }

  return url;
}

/**
 * Parse a create operation
 */
function parseCreateOperation(
  operationName: string,
  argsString: string
): ParsedOperation {
  // Get shape configuration, or default to generic shape for unknown types
  const shapeConfig = SHAPE_TYPE_MAPPING[operationName] || {
    type: 'shape',
    shapeName: operationName,
  };

  // Parse arguments
  const args = parseArguments(argsString);

  // Build shape object
  const shapeData = buildShapeData(shapeConfig, args);

  return {
    type: 'create',
    data: shapeData,
  };
}

/**
 * Parse an update operation
 */
function parseUpdateOperation(argsString: string): ParsedOperation {
  const args = parseArguments(argsString);

  if (args.positional.length === 0) {
    throw new Error(
      'Update operation requires an ID as the first argument. Example: update("shape1", property=value)'
    );
  }

  const id = args.positional[0];
  const properties = args.named;

  if (Object.keys(properties).length === 0) {
    throw new Error(
      `Update operation requires at least one property to update. Example: update("${id}", fill="#ff0000")`
    );
  }

  return {
    type: 'update',
    data: {
      id: String(id),
      ...properties,
    },
  };
}

/**
 * Parse a delete operation
 */
function parseDeleteOperation(argsString: string): ParsedOperation {
  const args = parseArguments(argsString);

  if (args.positional.length === 0) {
    throw new Error(
      'Delete operation requires an ID as the first argument. Example: delete("shape1")'
    );
  }

  if (args.positional.length > 1 || Object.keys(args.named).length > 0) {
    throw new Error(
      'Delete operation only accepts a single ID argument. Example: delete("shape1")'
    );
  }

  const id = args.positional[0];

  return {
    type: 'delete',
    data: [String(id)],
  };
}

/**
 * Parse a record operation
 */
function parseRecordOperation(argsString: string): ParsedOperation {
  const args = parseArguments(argsString);

  if (args.positional.length < 3) {
    throw new Error(
      'Record operation requires record ID, left, and top arguments. Example: record("MY-123", 100, 50)'
    );
  }

  const recordId = String(args.positional[0]);
  const left = parseInt(String(args.positional[1]), 10);
  const top = parseInt(String(args.positional[2]), 10);

  // Convert record ID to URL format
  const recordUrl = normalizeRecordUrl(recordId);

  return {
    type: 'record',
    data: {
      record_url: recordUrl,
      left,
      top,
    },
  };
}

/**
 * Parse a connector operation
 */
function parseConnectorOperation(argsString: string): ParsedOperation {
  const args = parseArguments(argsString);

  if (args.positional.length === 0) {
    throw new Error('Connector operation requires ID');
  }

  // Check that either attachment or positioning properties are provided
  // Note: We only check named args here. If positional args include position/points,
  // they'll be handled by the standard create operation parsing
  const hasAttachment = 'fromId' in args.named || 'toId' in args.named;
  const hasPoints = 'points' in args.named;
  const hasPositionalCoords = args.positional.length >= 3; // Has ID, left, top at minimum

  if (!hasAttachment && !hasPoints && !hasPositionalCoords) {
    throw new Error(
      'Connector requires either fromId/toId for attachment or points for positioning'
    );
  }

  // Delegate to standard create operation parsing
  return parseCreateOperation('connector', argsString);
}

/**
 * Parse a single line into an operation, or throw an error if invalid
 */
export function parseLine(line: string): ParsedOperation | null {
  const sanitizedLine = sanitizeLine(line);

  // Match function call syntax: function_name(args...)
  const match = sanitizedLine.match(/^(\w+)\((.*)\)$/);

  if (!match) {
    throw new Error(
      `Invalid syntax. Expected: shape_type(args...). Got: '${sanitizedLine}'. ` +
        'Use function call syntax like rect(id, 100, 50, "200x100", "text").'
    );
  }

  const operationName = match[1];
  const argsString = match[2];

  // Handle different operation types
  switch (operationName) {
    case 'update':
      return parseUpdateOperation(argsString);
    case 'delete':
      return parseDeleteOperation(argsString);
    case 'record':
      return parseRecordOperation(argsString);
    case 'connector':
      return parseConnectorOperation(argsString);
    default:
      // all other functions are create operations for the shape of that name
      return parseCreateOperation(operationName, argsString);
  }
}

export interface ParseResult {
  operations: ParsedOperation[];
  errors: ParseError[];
  newProcessedLines: number;
}

/**
 * Parse operations from raw content
 */
export function parseOperations(
  content: string,
  processedLines: number = 0,
  finished: boolean = false
): ParseResult {
  const operations: ParsedOperation[] = [];
  const errors: ParseError[] = [];

  // Split content into lines and identify complete lines
  const allLines = content.split('\n').map(line => line.trim());

  // Determine which lines to process
  const startIndex = processedLines;
  let endIndex: number;

  if (finished) {
    // When finished, process all lines
    endIndex = allLines.length - 1;
  } else {
    // we don't make any assumptions about the last line being complete until
    // streaming has finished, so exclude it until the subsequent line starts
    endIndex = allLines.length - 2;
  }

  // Only process if there are new lines
  const linesToProcess =
    startIndex >= 0 && startIndex <= endIndex
      ? allLines.slice(startIndex, endIndex + 1)
      : [];

  linesToProcess.forEach((line, relativeIndex) => {
    const lineNumber = startIndex + relativeIndex;

    // Skip empty lines and comments
    if (line === '' || line.startsWith('#')) {
      return;
    }

    try {
      const operation = parseLine(line);
      if (operation) {
        operations.push(operation);
      }
    } catch (error) {
      errors.push({
        position: lineNumber + 1,
        content: line,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  });

  // Update processed lines count only after processing
  const newProcessedLines =
    linesToProcess.length > 0 ? endIndex + 1 : processedLines;

  return {
    operations,
    errors,
    newProcessedLines,
  };
}
